import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ResponsiveService } from 'src/app/services/responsive/responsive.service';

/**
 * Dialog types available in the application
 */
export enum DialogType {
  /** Side panel dialog (852px width, right side) - Most common for add/edit forms */
  SIDE_PANEL = 'SIDE_PANEL',
  /** Center dialog (422px width) - For smaller dialogs */
  CENTER_SMALL = 'CENTER_SMALL',
  /** Center dialog (450px width) - For medium dialogs */
  CENTER_MEDIUM = 'CENTER_MEDIUM',
}

/**
 * Dialog configuration options
 */
export interface DialogConfig<T = unknown> {
  /** Dialog component type */
  component: any;
  /** Dialog type (SIDE_PANEL, CENTER_SMALL, CENTER_MEDIUM) */
  type?: DialogType;
  /** Data to pass to dialog */
  data?: T;
  /** Custom width (overrides type default) */
  width?: string;
  /** Custom height (overrides type default) */
  height?: string;
  /** Custom position (overrides type default) */
  position?: { right?: string; top?: string; bottom?: string; left?: string };
  /** Whether to auto focus */
  autoFocus?: boolean;
  /** Backdrop class */
  backdropClass?: string;
  /** Whether to disable close */
  disableClose?: boolean;
  /** Whether to close on navigation */
  closeOnNavigation?: boolean;
  /** Panel CSS class (e.g. for mobile overrides) */
  panelClass?: string | string[];
  /** Max width */
  maxWidth?: string;
}

/**
 * Dialog preset configuration interface
 */
interface DialogPreset {
  width: string;
  height?: string;
  position?: { right?: string; top?: string; bottom?: string; left?: string };
  backdropClass: string;
  autoFocus: boolean;
}

/**
 * Dialog configuration presets
 */
const DIALOG_PRESETS: Record<DialogType, DialogPreset> = {
  [DialogType.SIDE_PANEL]: {
    width: '852px',
    height: '100%',
    position: { right: '25px', top: '0px', bottom: '0px' },
    backdropClass: 'backdropBackground',
    autoFocus: false,
  },
  [DialogType.CENTER_SMALL]: {
    width: '422px',
    backdropClass: 'backdropBackground',
    autoFocus: false,
  },
  [DialogType.CENTER_MEDIUM]: {
    width: '450px',
    backdropClass: 'backdropBackground',
    autoFocus: false,
  },
};

/**
 * Generic Dialog Service
 *
 * Provides centralized dialog opening functionality to eliminate code duplication.
 */
@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private matDialog = inject(MatDialog);
  private readonly responsiveService = inject(ResponsiveService);

  /**
   * On mobile, keep fixed-width dialogs from overflowing the viewport.
   * Side panels become full-screen sheets; centered dialogs fit within a small margin.
   * Explicit non-px consumer widths (e.g. '95%') are preserved.
   */
  private applyMobileSizing(dialogConfig: any, type: DialogType): void {
    if (!this.responsiveService.isMobile) {
      return;
    }

    if (type === DialogType.SIDE_PANEL) {
      dialogConfig.width = '100vw';
      dialogConfig.maxWidth = '100vw';
      // Content-height on mobile (the surface caps at 95vh and scrolls) instead of
      // forcing full height, which left a large blank area before the footer.
      delete dialogConfig.height;
      delete dialogConfig.position;
    } else {
      dialogConfig.maxWidth = '95vw';
      if (
        !dialogConfig.width ||
        (typeof dialogConfig.width === 'string' &&
          dialogConfig.width.endsWith('px'))
      ) {
        dialogConfig.width = '95vw';
      }
    }
  }

  /** Number of dialogs currently open (for auth/logout flow) */
  get openDialogsLength(): number {
    return this.matDialog.openDialogs.length;
  }

  /** Close all open dialogs (e.g. on logout) */
  closeAll(): void {
    this.matDialog.closeAll();
  }

  openSidePanel<T = unknown, R = unknown>(
    component: any,
    data?: T
  ): Observable<R | undefined> {
    return this.open<R>({
      component,
      type: DialogType.SIDE_PANEL,
      data,
    });
  }

  openCenterSmall<T = unknown, R = unknown>(
    component: any,
    data?: T
  ): Observable<R | undefined> {
    return this.open<R>({
      component,
      type: DialogType.CENTER_SMALL,
      data,
    });
  }

  openCenterMedium<T = unknown, R = unknown>(
    component: any,
    data?: T
  ): Observable<R | undefined> {
    return this.open<R>({
      component,
      type: DialogType.CENTER_MEDIUM,
      data,
    });
  }

  open<R = unknown>(config: DialogConfig): Observable<R | undefined> {
    const { component, type = DialogType.SIDE_PANEL, ...customConfig } = config;
    const preset = DIALOG_PRESETS[type];

    const dialogConfig: any = {
      width: customConfig.width || preset.width,
      backdropClass: customConfig.backdropClass || preset.backdropClass,
      autoFocus: customConfig.autoFocus !== undefined ? customConfig.autoFocus : preset.autoFocus,
      data: customConfig.data,
    };

    if (customConfig.height || preset.height) {
      dialogConfig.height = customConfig.height || preset.height;
    }
    if (customConfig.position || preset.position) {
      dialogConfig.position = customConfig.position || preset.position;
    }
    if (customConfig.disableClose !== undefined) {
      dialogConfig.disableClose = customConfig.disableClose;
    }
    if (customConfig.closeOnNavigation !== undefined) {
      dialogConfig.closeOnNavigation = customConfig.closeOnNavigation;
    }
    if (customConfig.panelClass !== undefined) {
      dialogConfig.panelClass = customConfig.panelClass;
    }
    if (customConfig.maxWidth !== undefined) {
      dialogConfig.maxWidth = customConfig.maxWidth;
    }

    Object.keys(dialogConfig).forEach((key) => {
      if (dialogConfig[key] === undefined) {
        delete dialogConfig[key];
      }
    });

    this.applyMobileSizing(dialogConfig, type);

    const dialogRef: MatDialogRef<any, R> = this.matDialog.open(component, dialogConfig);
    return dialogRef.afterClosed();
  }

  openWithRef<R = unknown>(config: DialogConfig): MatDialogRef<any, R> {
    const { component, type = DialogType.SIDE_PANEL, ...customConfig } = config;
    const preset = DIALOG_PRESETS[type];

    const dialogConfig: any = {
      width: customConfig.width || preset.width,
      backdropClass: customConfig.backdropClass || preset.backdropClass,
      autoFocus: customConfig.autoFocus !== undefined ? customConfig.autoFocus : preset.autoFocus,
      data: customConfig.data,
    };

    if (customConfig.height || preset.height) {
      dialogConfig.height = customConfig.height || preset.height;
    }
    if (customConfig.position || preset.position) {
      dialogConfig.position = customConfig.position || preset.position;
    }
    if (customConfig.disableClose !== undefined) {
      dialogConfig.disableClose = customConfig.disableClose;
    }
    if (customConfig.closeOnNavigation !== undefined) {
      dialogConfig.closeOnNavigation = customConfig.closeOnNavigation;
    }
    if (customConfig.panelClass !== undefined) {
      dialogConfig.panelClass = customConfig.panelClass;
    }
    if (customConfig.maxWidth !== undefined) {
      dialogConfig.maxWidth = customConfig.maxWidth;
    }

    Object.keys(dialogConfig).forEach((key) => {
      if (dialogConfig[key] === undefined) {
        delete dialogConfig[key];
      }
    });

    this.applyMobileSizing(dialogConfig, type);

    return this.matDialog.open(component, dialogConfig);
  }
}
